import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — Mail Inbox  (Phase 5.4 C)
//
// The user's own IMAP mailbox, with inbound mail filed against the matching
// contact and replies sent from their own address. Laid out the way any real
// mail client is: a folder sidebar, a compact list, and a full-page reading
// view with Reply/Forward — not a flat list of cards.
//
// The password field is write-only. It is never populated from the server —
// not with the value, not with a masked placeholder that could be saved back —
// and leaving it blank keeps whatever is stored.
//
// Mail that matched no contact is counted and filterable rather than left
// silently off every timeline, because "it's in the CRM somewhere" is how a
// customer thread gets lost.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { SignatureEditor } from '../components/SignatureEditor';
import { toast } from 'react-hot-toast';
import {
  Inbox, Settings2, RefreshCcw, Send, AlertTriangle, Link2, Mail,
  PenSquare, X, ArrowLeft, Reply, Forward, History,
} from 'lucide-react';

type Folder = 'inbox' | 'sent' | 'all' | 'unmatched';
type Mode = 'list' | 'detail' | 'settings';

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) : '';

/** Today: just the time, like a real inbox. Older: the date. */
const fmtShort = (d?: string) => {
  if (!d) return '';
  const date = new Date(d);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/** A handful of pleasant, distinct tones — picked by a hash of the sender so
 *  the same address always lands on the same colour without maintaining a map. */
const AVATAR_TONES = [
  'bg-info/10 text-info-text dark:bg-info dark:text-info-text',
  'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  'bg-success/10 text-success-text dark:bg-success dark:text-success-text',
  'bg-warning/10 text-warning-text dark:bg-warning dark:text-warning-text',
  'bg-danger/10 text-danger-text dark:bg-danger dark:text-danger-text',
  'bg-info/10 text-info-text dark:bg-info dark:text-info-text',
];

function toneFor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

function SenderAvatar({ name, email }: { name?: string; email?: string }) {
  const label = (name || email || '?').trim();
  const initial = label.charAt(0).toUpperCase();
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${toneFor(email || label)}`}>
      {initial}
    </div>
  );
}

export function MailInboxPage() {
  const [mode, setMode] = useState<Mode>('list');
  const [folder, setFolder] = useState<Folder>('inbox');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [composeDraft, setComposeDraft] = useState<{ to?: string; subject?: string; body?: string } | null>(null);
  const qc = useQueryClient();

  const { data: cfg } = useQuery({
    queryKey: ['mailbox', 'config'],
    queryFn: async () => (await api.get('/api/mailbox/config')).data,
  });
  const { data } = useQuery({
    queryKey: ['mailbox', 'messages'],
    queryFn: async () => (await api.get('/api/mailbox/messages')).data,
    // The IMAP fetch itself runs server-side on its own 5-minute timer
    // (startMailboxPoller, server.js) — mail is already sitting in the
    // database well before anyone looks. What was missing was the BROWSER
    // ever asking again: the query client's global staleTime/gcTime meant an
    // already-open tab kept showing whatever it fetched on load until
    // something explicitly invalidated it (Fetch now, a reply, opening a
    // message) — new mail existed but nothing told this tab to go look.
    // Re-querying every 60s is a plain DB read (GET /api/mailbox/messages),
    // not a fresh IMAP connection, so it stays cheap even while idle.
    refetchInterval: 60_000,
    // Coming back to the tab is also a natural moment to check — overrides
    // the app-wide default of false (most screens don't want a background
    // refetch just from refocusing; a mailbox does).
    refetchOnWindowFocus: true,
  });

  const poll = useMutation({
    mutationFn: async () => (await api.post('/api/mailbox/poll')).data,
    onSuccess: (d: any) => {
      if (d.ok) {
        // `matched` is reported separately from `stored`: mail filed against no
        // contact is invisible on every timeline, and that number matters.
        toast.success(`Fetched ${d.fetched}, stored ${d.stored}, matched to a contact ${d.matched}.`);
      } else toast.error(d.error || 'Fetch failed.');
      qc.invalidateQueries({ queryKey: ['mailbox', 'messages'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Fetch failed.'),
  });

  const fullSync = useMutation({
    // A full backfill can take a while on a mailbox with years of history —
    // the routine poll's 30s default would cut it off mid-import.
    mutationFn: async () => (await api.post('/api/mailbox/poll/full', {}, { timeout: 10 * 60 * 1000 })).data,
    onSuccess: (d: any) => {
      if (d.ok) toast.success(`Imported the full mailbox: ${d.fetched} seen, ${d.stored} new, ${d.matched} matched to a contact.`);
      else toast.error(d.error || 'Full import failed.');
      qc.invalidateQueries({ queryKey: ['mailbox', 'messages'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Full import failed.'),
  });

  const configured = Boolean(cfg?.config?.host);
  const allItems: any[] = data?.items || [];
  const items = folder === 'inbox' ? allItems.filter((m) => m.direction === 'in')
    : folder === 'sent' ? allItems.filter((m) => m.direction === 'out')
    : folder === 'unmatched' ? allItems.filter((m) => !m.contactId)
    : allItems;

  const inboxUnread = allItems.filter((m) => m.direction === 'in' && !m.isRead).length;
  const unmatchedCount = data?.unmatchedCount ?? allItems.filter((m) => !m.contactId).length;
  const selected = selectedId ? allItems.find((m) => m._id === selectedId) : null;

  const goFolder = (f: Folder) => { setFolder(f); setMode('list'); setSelectedId(null); };
  const openMessage = (id: string) => { setSelectedId(id); setMode('detail'); };
  const backToList = () => { setMode('list'); setSelectedId(null); };
  const openCompose = () => { setComposeDraft(null); setComposing(true); };
  const openForward = (message: any, body: string) => {
    setComposeDraft({
      to: '',
      subject: /^fwd:/i.test(message.subject || '') ? message.subject : `Fwd: ${message.subject || '(no subject)'}`,
      body: `\n\n---------- Forwarded message ----------\nFrom: ${message.fromName || message.from}\nDate: ${fmt(message.receivedAt)}\nSubject: ${message.subject || '(no subject)'}\n\n${body}`,
    });
    setComposing(true);
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader icon={Inbox} eyebrow="Workspace" title="Mail Inbox" description={<>Your own mailbox, with messages filed against the matching contact.</>} />

      {cfg && !cfg.encryptionAvailable && (
        <div className="p-4 rounded-xl bg-danger/10 dark:bg-danger border border-danger/25 dark:border-danger/40 text-sm text-danger-text dark:text-danger-text flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-semibold">Secret encryption is not configured on this server.</p>
            <p className="text-xs mt-1">
              A mailbox password will not be accepted until it is — storing your real mail password
              unencrypted is not something this will do.
            </p>
          </div>
        </div>
      )}

      {cfg?.config?.lastError && (
        <div className="p-3 rounded-xl bg-warning/10 dark:bg-warning border border-warning/25 dark:border-warning/40 text-xs text-warning-text dark:text-warning-text">
          Last fetch failed: {cfg.config.lastError}
          {cfg.config.consecutiveFailures >= 10 && ' — the mailbox has been switched off after repeated failures.'}
        </div>
      )}

      <div className="flex h-[75vh] min-h-[560px] rounded-xl border border-outline bg-surface overflow-hidden">
        <Sidebar
          configured={configured}
          folder={folder}
          mode={mode}
          onFolder={goFolder}
          onCompose={openCompose}
          onSettings={() => setMode('settings')}
          inboxUnread={inboxUnread}
          unmatchedCount={unmatchedCount}
        />

        <div className="flex-1 min-w-0 flex flex-col">
          {!configured && mode !== 'settings' ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center text-sm text-on-surface-variant">
              No mailbox configured yet. Open Mailbox settings to connect yours.
            </div>
          ) : mode === 'settings' ? (
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <SignatureEditor
                signature={cfg?.config?.signature || ''}
                canSend={Boolean(cfg?.config?.smtpHost && cfg?.config?.username)}
                onSaved={() => qc.invalidateQueries({ queryKey: ['mailbox', 'config'] })}
              />
              <MailboxConfig
                config={cfg?.config}
                onSaved={() => { qc.invalidateQueries({ queryKey: ['mailbox'] }); setMode('list'); }}
              />
            </div>
          ) : mode === 'detail' && selected ? (
            <MessageDetail message={selected} onBack={backToList} onForward={openForward} />
          ) : (
            <MessageList
              items={items} folder={folder} onOpen={openMessage}
              onRefresh={() => poll.mutate()} refreshing={poll.isPending}
              onFullSync={() => fullSync.mutate()} syncingFull={fullSync.isPending}
            />
          )}
        </div>
      </div>

      {composing && (
        <ComposeModal
          initial={composeDraft || undefined}
          onClose={() => setComposing(false)}
          onSent={() => { setComposing(false); qc.invalidateQueries({ queryKey: ['mailbox', 'messages'] }); }}
        />
      )}
    </div>
  );
}

function Sidebar({
  configured, folder, mode, onFolder, onCompose, onSettings, inboxUnread, unmatchedCount,
}: {
  configured: boolean; folder: Folder; mode: Mode;
  onFolder: (f: Folder) => void; onCompose: () => void; onSettings: () => void;
  inboxUnread: number; unmatchedCount: number;
}) {
  const nav: { key: Folder; label: string; icon: any; count: number }[] = [
    { key: 'inbox', label: 'Inbox', icon: Inbox, count: inboxUnread },
    { key: 'sent', label: 'Sent', icon: Send, count: 0 },
    { key: 'all', label: 'All mail', icon: Mail, count: 0 },
    { key: 'unmatched', label: 'Unmatched', icon: Link2, count: unmatchedCount },
  ];
  return (
    <div className="w-52 shrink-0 border-r border-outline bg-surface-variant/20 flex flex-col p-3">
      <button onClick={onCompose} disabled={!configured}
        className="mb-4 px-4 py-2.5 rounded-full bg-primary text-on-primary text-sm font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-40">
        <PenSquare className="w-4 h-4" /> Compose
      </button>

      <nav className="space-y-1 flex-1">
        {nav.map((it) => (
          <button key={it.key} onClick={() => onFolder(it.key)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-full text-sm transition-colors ${
              mode !== 'settings' && folder === it.key
                ? 'bg-primary/15 text-primary font-bold'
                : 'text-on-surface-variant hover:bg-surface-variant/60 font-medium'
            }`}>
            <it.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left truncate">{it.label}</span>
            {it.count > 0 && <span className="text-xs font-semibold">{it.count}</span>}
          </button>
        ))}
      </nav>

      <button onClick={onSettings}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
          mode === 'settings' ? 'bg-primary/15 text-primary font-bold' : 'text-on-surface-variant hover:bg-surface-variant/60'
        }`}>
        <Settings2 className="w-4 h-4 shrink-0" /> Mailbox settings
      </button>
    </div>
  );
}

function MessageList({
  items, folder, onOpen, onRefresh, refreshing, onFullSync, syncingFull,
}: {
  items: any[]; folder: Folder; onOpen: (id: string) => void;
  onRefresh: () => void; refreshing: boolean;
  onFullSync: () => void; syncingFull: boolean;
}) {
  const title = folder === 'inbox' ? 'Inbox' : folder === 'sent' ? 'Sent' : folder === 'unmatched' ? 'Unmatched' : 'All mail';
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-outline">
        <h2 className="text-sm font-bold">{title}</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              if (syncingFull) return;
              if (window.confirm('Import every message Zoho has for this mailbox, not just recent mail? This can take a while for a large inbox.')) onFullSync();
            }}
            disabled={syncingFull} title="Import the mailbox's entire history, not just recent mail"
            className="px-2.5 py-1.5 rounded-full hover:bg-surface-variant/60 disabled:opacity-40 flex items-center gap-1.5 text-xs font-semibold text-on-surface-variant">
            <History className={`w-3.5 h-3.5 ${syncingFull ? 'animate-spin' : ''}`} />
            {syncingFull ? 'Importing…' : 'Import all mail'}
          </button>
          <button onClick={onRefresh} disabled={refreshing} title="Fetch now"
            className="p-1.5 rounded-full hover:bg-surface-variant/60 disabled:opacity-40">
            <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-on-surface-variant">
            {folder === 'unmatched' ? 'Every message is linked to a contact.' : 'No messages here. Try Fetch now.'}
          </div>
        ) : (
          items.map((m) => <MessageListRow key={m._id} message={m} onOpen={() => onOpen(m._id)} />)
        )}
      </div>
    </div>
  );
}

function MessageListRow({ message, onOpen }: { message: any; onOpen: () => void }) {
  const unread = message.direction === 'in' && !message.isRead;
  return (
    <button onClick={onOpen}
      className={`w-full flex items-center gap-3 px-4 py-2.5 border-b border-outline/60 text-left transition-colors hover:bg-surface-variant/30 ${
        unread ? 'bg-primary/[0.03]' : ''
      }`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${unread ? 'bg-primary' : 'bg-transparent'}`} />
      <SenderAvatar name={message.fromName} email={message.from} />
      <span className={`w-40 shrink-0 truncate text-sm ${unread ? 'font-bold' : 'font-medium'}`}>
        {message.direction === 'out' ? `To: ${message.to?.[0] || ''}` : (message.fromName || message.from)}
      </span>
      <span className="flex-1 min-w-0 truncate text-sm">
        <span className={unread ? 'font-semibold' : ''}>{message.subject || '(no subject)'}</span>
        <span className="text-on-surface-variant"> — {message.preview}</span>
      </span>
      {!message.contactId && <Link2 className="w-3.5 h-3.5 text-warning-text shrink-0" />}
      <span className="text-xs text-on-surface-variant shrink-0 w-20 text-right">{fmtShort(message.receivedAt)}</span>
    </button>
  );
}

function MessageDetail({
  message, onBack, onForward,
}: { message: any; onBack: () => void; onForward: (message: any, body: string) => void }) {
  const qc = useQueryClient();
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');

  // The list endpoint deliberately omits `body` (it's a lightweight summary),
  // so opening a message fetches its full thread — every message that shares
  // this conversation, in order, complete text.
  const thread = useQuery({
    queryKey: ['mailbox', 'thread', message.threadKey],
    queryFn: async () => (await api.get(`/api/mailbox/thread/${encodeURIComponent(message.threadKey)}`)).data,
    enabled: Boolean(message.threadKey),
  });

  const markRead = useMutation({
    mutationFn: async () => (await api.post(`/api/mailbox/messages/${message._id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mailbox', 'messages'] }),
  });

  useEffect(() => {
    if (message.direction === 'in' && !message.isRead) markRead.mutate();
    // Runs once per opened message — re-firing on every render would spam the
    // mark-read endpoint for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message._id]);

  const reply = useMutation({
    mutationFn: async () => (await api.post(`/api/mailbox/reply/${message._id}`, { body: replyBody })).data,
    onSuccess: () => {
      toast.success('Reply sent.');
      setReplying(false); setReplyBody('');
      qc.invalidateQueries({ queryKey: ['mailbox', 'messages'] });
      qc.invalidateQueries({ queryKey: ['mailbox', 'thread', message.threadKey] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not send the reply.'),
  });

  const threadItems = thread.data?.items || [];
  const latest = threadItems[threadItems.length - 1] || message;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-outline">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-surface-variant/60">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h2 className="text-sm font-bold truncate">{message.subject || '(no subject)'}</h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
        {thread.isLoading ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : threadItems.length === 0 ? (
          <ThreadMessageCard message={message} expanded />
        ) : (
          threadItems.map((t: any, i: number) => (
            <ThreadMessageCard key={t._id} message={t} expanded={i === threadItems.length - 1} />
          ))
        )}

        {!replying && message.direction === 'in' && (
          <div className="flex gap-2 pt-2">
            <button onClick={() => setReplying(true)}
              className="px-4 py-2 rounded-full border border-outline text-sm font-semibold flex items-center gap-2 hover:bg-surface-variant/40">
              <Reply className="w-4 h-4" /> Reply
            </button>
            <button onClick={() => onForward(latest, latest.body || latest.preview || '')}
              className="px-4 py-2 rounded-full border border-outline text-sm font-semibold flex items-center gap-2 hover:bg-surface-variant/40">
              <Forward className="w-4 h-4" /> Forward
            </button>
          </div>
        )}

        {replying && (
          <div className="border border-outline rounded-xl p-3 space-y-2">
            <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={6}
              placeholder={`Reply to ${message.from}…`}
              className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {/* Sent from the user's own account, not a shared system mailbox —
                  the customer wrote to a person and should hear back from them. */}
              <p className="text-[11px] text-on-surface-variant">
                Sent from your own address, threaded onto this conversation.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setReplying(false)}
                  className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold">
                  Cancel
                </button>
                <button onClick={() => reply.mutate()} disabled={!replyBody.trim() || reply.isPending}
                  className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40">
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One message inside an open thread — collapsed to a single line until
 *  clicked, exactly like Gmail's own conversation view, with the newest
 *  message starting expanded. */
function ThreadMessageCard({ message, expanded }: { message: any; expanded: boolean }) {
  const [open, setOpen] = useState(expanded);
  return (
    <div className={`rounded-xl border ${open ? 'border-outline' : 'border-transparent'}`}>
      <div
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer ${open ? 'bg-surface' : 'hover:bg-surface-variant/40'}`}
        onClick={() => setOpen(!open)}
      >
        <SenderAvatar name={message.fromName} email={message.from} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-semibold truncate">
              {message.direction === 'out' ? 'You' : (message.fromName || message.from)}
              {/* The address, not just the display name — a "From" header's
                  display name is whatever the SENDING account typed into it,
                  never verified against who actually holds that mailbox. This
                  is what makes it visible when the two disagree, instead of
                  the address only being discoverable by asking someone to
                  check the raw message. */}
              {message.direction !== 'out' && message.fromName && (
                <span className="ml-1.5 font-normal text-on-surface-variant">&lt;{message.from}&gt;</span>
              )}
            </p>
            <span className="text-[11px] text-on-surface-variant shrink-0">{fmt(message.receivedAt)}</span>
          </div>
          {open ? (
            <p className="text-xs text-on-surface-variant truncate">
              to {message.direction === 'out' ? (message.to || []).join(', ') : 'me'}
            </p>
          ) : (
            <p className="text-xs text-on-surface-variant truncate">{message.preview}</p>
          )}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1">
          <p className="text-sm whitespace-pre-wrap">{message.body || message.preview}</p>
        </div>
      )}
    </div>
  );
}

function ComposeModal({
  initial, onClose, onSent,
}: { initial?: { to?: string; subject?: string; body?: string }; onClose: () => void; onSent: () => void }) {
  const [to, setTo] = useState(initial?.to || '');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState(initial?.body || '');

  const send = useMutation({
    mutationFn: async () => (await api.post('/api/mailbox/send', { to, subject, body })).data,
    onSuccess: () => { toast.success('Message sent.'); onSent(); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not send the message.'),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl border border-outline bg-surface shadow-xl mt-16"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-outline">
          <h3 className="text-sm font-bold flex items-center gap-2"><PenSquare className="w-4 h-4" /> New message</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-variant/60"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-2">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="To — recipient's email address"
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} placeholder="Write your message…"
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
          <p className="text-[11px] text-on-surface-variant">Your signature (Mailbox settings) is added automatically.</p>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-outline">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold">
            Cancel
          </button>
          <button onClick={() => send.mutate()} disabled={!to.trim() || !body.trim() || send.isPending}
            className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

function MailboxConfig({ config, onSaved }: { config: any; onSaved: () => void }) {
  const [form, setForm] = useState({
    host: config?.host || '', port: config?.port || 993, secure: config?.secure !== false,
    username: config?.username || '', password: '',
    folder: config?.folder || 'INBOX',
    smtpHost: config?.smtpHost || '', smtpPort: config?.smtpPort || 587,
    smtpSecure: Boolean(config?.smtpSecure), fromName: config?.fromName || '',
    isActive: Boolean(config?.isActive),
  });

  const save = useMutation({
    mutationFn: async () => {
      // A blank password means "leave it alone", so it is omitted rather than
      // sent as an empty string that would wipe the stored credential.
      const { password, ...rest } = form;
      return (await api.put('/api/mailbox/config', password ? form : rest)).data;
    },
    onSuccess: () => { toast.success('Mailbox saved.'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save.'),
  });

  const test = useMutation({
    mutationFn: async () => (await api.post('/api/mailbox/test')).data,
    onSuccess: (d: any) => (d.ok ? toast.success(d.message) : toast.error(d.error, { duration: 8000 })),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Test failed.'),
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 space-y-3 max-w-2xl">
      <h3 className="text-sm font-bold">Mailbox settings</h3>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="IMAP host" value={form.host} onChange={(v) => set('host', v)} />
        <Field label="Port" value={form.port} onChange={(v) => set('port', Number(v) || 0)} />
        <label className="flex items-end gap-2 text-sm pb-2">
          <input type="checkbox" checked={form.secure} onChange={(e) => set('secure', e.target.checked)} />
          Use TLS
        </label>
        <Field label="Username" value={form.username} onChange={(v) => set('username', v)} />
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Password</span>
          {/* Write-only. Never populated from the server — not the value, and
              not a masked placeholder that could be saved back as the password. */}
          <input type="password" value={form.password} onChange={(e) => set('password', e.target.value)}
            placeholder={config?.hasPassword ? 'Stored — leave blank to keep it' : 'Enter your mailbox password'}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
        </label>
        <Field label="Folder" value={form.folder} onChange={(v) => set('folder', v)} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="SMTP host (for replies)" value={form.smtpHost} onChange={(v) => set('smtpHost', v)} />
        <Field label="SMTP port" value={form.smtpPort} onChange={(v) => set('smtpPort', Number(v) || 0)} />
        <Field label="From name" value={form.fromName} onChange={(v) => set('fromName', v)} />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
        Poll this mailbox automatically
      </label>

      <p className="text-[11px] text-on-surface-variant">
        The password is encrypted before it is stored and is never sent back to this page.
      </p>

      <div className="flex justify-end gap-2">
        <button onClick={() => test.mutate()} disabled={test.isPending}
          className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold disabled:opacity-40">
          Test connection
        </button>
        <button onClick={() => save.mutate()} disabled={save.isPending}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
          Save
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: any; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold block mb-1">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
    </label>
  );
}

export default MailInboxPage;
